import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tent, Trash2, Loader2 } from "lucide-react";

/** Semana(s) de campamento de verano incluidas en el plan y cuándo se han usado. */
export const CampamentoAlumno = ({ alumno, onCambio }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ desde: "", hasta: "", nota: "" });
  const [enviando, setEnviando] = useState(false);

  const incluidas = alumno.campamento_incluido || 0;
  const usos = alumno.campamento_usos || [];
  const pendientes = Math.max(incluidas - usos.length, 0);

  const guardar = async () => {
    setEnviando(true);
    try {
      await api.post(`/alumnos/${alumno.id}/campamento`, form);
      toast.success("Semana de campamento registrada en la ficha");
      setForm({ desde: "", hasta: "", nota: "" });
      setOpen(false);
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setEnviando(false);
    }
  };

  const borrar = async (usoId) => {
    try {
      await api.delete(`/alumnos/${alumno.id}/campamento/${usoId}`);
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!incluidas && usos.length === 0) return null;

  return (
    <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-campamento">
      <div className="flex items-center gap-2 mb-3">
        <Tent className="h-5 w-5 text-primary" />
        <h3 className="font-display text-lg font-bold">Semana de campamento de verano</h3>
      </div>
      <Badge variant={pendientes ? "default" : "secondary"} data-testid="campamento-badge">
        {pendientes ? `${pendientes} semana(s) pendiente(s) de usar` : "Ya disfrutada"}
      </Badge>
      <p className="text-sm text-muted-foreground mt-3">
        Incluida en {alumno.campamento_plan || alumno.plan_nombre || "su plan"} ·{" "}
        {usos.length} de {incluidas} usada(s)
      </p>

      <div className="mt-4 space-y-2">
        {usos.map((u) => (
          <div key={u.id} className="flex items-start justify-between gap-3 text-sm border-b border-border pb-2 last:border-0"
            data-testid={`campamento-uso-${u.id}`}>
            <span>
              <span className="font-semibold">{u.desde} → {u.hasta}</span>
              {u.nota && <span className="text-muted-foreground"> · {u.nota}</span>}
            </span>
            <button onClick={() => borrar(u.id)} data-testid={`campamento-borrar-${u.id}`}>
              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors duration-200" />
            </button>
          </div>
        ))}
        {usos.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="campamento-sin-usos">
            Todavía no ha usado su semana de campamento.
          </p>
        )}
      </div>

      <Button className="rounded-full font-semibold mt-5 w-full" disabled={!pendientes}
        onClick={() => setOpen(true)} data-testid="campamento-registrar-btn">
        <Tent className="h-4 w-4 mr-2" /> Registrar semana usada
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl" data-testid="campamento-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Semana de campamento</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Desde</Label>
              <Input type="date" value={form.desde} data-testid="campamento-desde-input"
                onChange={(e) => setForm({ ...form, desde: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Hasta</Label>
              <Input type="date" value={form.hasta} data-testid="campamento-hasta-input"
                onChange={(e) => setForm({ ...form, hasta: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Nota (opcional)</Label>
              <Textarea value={form.nota} data-testid="campamento-nota-input"
                placeholder="Turno de mañana, grupo de acuarela…"
                onChange={(e) => setForm({ ...form, nota: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardar}
              disabled={enviando || !form.desde || !form.hasta} data-testid="campamento-guardar-btn">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
